// ---------------------------------------------------------------------------
// A micro-stepper: numbered dots joined by thin lines (NDI-239 v2)
//
// A small LitElement, <sdr-micro-stepper>, in the light DOM so the app's
// stylesheet reaches it. It holds no state: the caller passes the items (see
// stepperItems in setup-model.ts) and what a click does; microStepper() is
// the one-line way to place it. A done step shows a check (icon() from the
// design system, inline SVG, styled without the system's CSS), the current
// one is navy, todo is a plain ring, blocked a faded dashed one. A blocked
// step stays a focusable button (aria-disabled, not disabled — a disabled
// button is skipped by Tab and shows no tooltip on touch, R37) described by
// the server's (German) reason, which is also its title for the mouse; a
// click on it calls onBlocked, so the caller can show the reason (on touch
// too). Every dot is a real button,
// so Tab and Enter work and screen readers hear "Step 2: Inputs"; the current
// one carries aria-current="step". On a phone only the numbers show.
//
// Colours only from the theme's variables (look.ts says why).
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type TemplateResult } from 'lit';

import { icon } from '../design/icons.js';
import type { StepperItem } from './setup-model.js';

const DOT: Record<StepperItem['look'], string> = {
  current: 'border-(--color-primary) bg-(--color-primary) text-(--color-primary-content)',
  done: 'border-(--sdr-ink-muted) bg-(--color-base-100) text-(--color-base-content)',
  todo: 'border-(--sdr-line-strong) bg-(--color-base-100) text-(--sdr-ink-muted)',
  // Blocked looks like it: a dashed ring, faded — not the same dot as an open step.
  blocked: 'border-dashed border-(--sdr-line-strong) bg-transparent text-(--sdr-ink-subtle) opacity-60',
};

const LABEL: Record<StepperItem['look'], string> = {
  current: 'text-(--color-base-content) font-medium',
  done: 'text-(--color-base-content)',
  todo: 'text-(--sdr-ink-muted)',
  blocked: 'text-(--sdr-ink-subtle) opacity-60',
};

export class SdrMicroStepper extends LitElement {
  static override properties = {
    items: { attribute: false },
    label: { attribute: false },
    onSelect: { attribute: false },
    onBlocked: { attribute: false },
  };

  declare items: StepperItem[];
  /** The list's accessible name, e.g. "Setup steps". */
  declare label: string;
  declare onSelect: ((key: string) => void) | undefined;
  /** A click on a blocked step: the caller shows why (a tooltip does not exist on touch). */
  declare onBlocked: ((item: StepperItem) => void) | undefined;

  constructor() {
    super();
    this.items = [];
    this.label = 'Steps';
  }

  override createRenderRoot() {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    if (!this.style.display) this.style.display = 'block';
  }

  override render(): TemplateResult {
    const items = this.items ?? [];
    const idBase = this.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return html`
      <nav aria-label=${this.label}>
        <ol class="flex items-center">
          ${items.map(
            (item, i) => html`
              ${
                i > 0
                  ? html`<li
                    data-step-line
                    aria-hidden="true"
                    class="${
                      item.look === 'done' || items[i - 1]?.look === 'done' ? 'bg-(--sdr-ink-muted)' : 'bg-(--sdr-line)'
                    } mx-1.5 h-px min-w-3 flex-1 sm:mx-2"
                  ></li>`
                  : nothing
              }
              <li class="flex shrink-0">
                <button
                  type="button"
                  class="group flex items-center gap-2 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-(--sdr-hover) aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent"
                  aria-label="Step ${item.number}: ${item.label}"
                  aria-current=${item.look === 'current' ? 'step' : nothing}
                  aria-disabled=${item.reachable ? nothing : 'true'}
                  aria-describedby=${!item.reachable && item.reason ? `${idBase}-${item.key}-why` : nothing}
                  title=${!item.reachable && item.reason ? item.reason : nothing}
                  @click=${() => {
                    if (item.reachable) this.onSelect?.(item.key);
                    else this.onBlocked?.(item);
                  }}
                >
                  <span
                    class="${DOT[item.look]} flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium"
                    >${item.look === 'done' ? icon({ name: 'check', size: 14 }) : item.number}</span
                  >
                  <span class="${LABEL[item.look]} hidden text-sm sm:inline">${item.label}</span>
                </button>
                ${
                  !item.reachable && item.reason
                    ? html`<span id="${idBase}-${item.key}-why" class="sr-only" lang="de">${item.reason}</span>`
                    : nothing
                }
              </li>
            `,
          )}
        </ol>
      </nav>
    `;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('sdr-micro-stepper')) {
  customElements.define('sdr-micro-stepper', SdrMicroStepper);
}

declare global {
  interface HTMLElementTagNameMap {
    'sdr-micro-stepper': SdrMicroStepper;
  }
}

/** Places a micro-stepper: the items, the list's name, what choosing a reachable step does, and a blocked one. */
export function microStepper(p: {
  items: StepperItem[];
  /** The list's accessible name, e.g. "Setup steps". */
  label: string;
  onSelect: (key: string) => void;
  onBlocked?: (item: StepperItem) => void;
}): TemplateResult {
  return html`<sdr-micro-stepper
    .items=${p.items}
    .label=${p.label}
    .onSelect=${p.onSelect}
    .onBlocked=${p.onBlocked}
  ></sdr-micro-stepper>`;
}
